import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CrossIcon } from '../components/Icons';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import { authApi } from '../api/client';
import './AuthScreen.css';

// ── Social provider SVG icons ────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="17" height="20" viewBox="0 0 814 1000" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.5 269-317.5 70.5 0 129.4 46.3 173.8 46.3 42.8 0 109.8-49.1 189.8-49.1 30.8 0 108.2 2.6 159.6 96.3zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
    </svg>
  );
}

// ── Divider ──────────────────────────────────────────────────────────────────
function OrDivider({ label }) {
  return (
    <div className="auth-divider">
      <span className="auth-divider-line"/>
      <span className="auth-divider-text">{label || 'or'}</span>
      <span className="auth-divider-line"/>
    </div>
  );
}

function SocialButtons({ t, onGoogle, onApple, loading }) {
  return (
    <div className="auth-social-btns">
      <button type="button" className="auth-social-btn google" onClick={onGoogle} disabled={loading}>
        <GoogleIcon/> {t ? t('auth.continueWithGoogle') : 'Continue with Google'}
      </button>
      <button type="button" className="auth-social-btn apple" onClick={onApple} disabled={loading}>
        <AppleIcon/> {t ? t('auth.continueWithApple') : 'Continue with Apple'}
      </button>
    </div>
  );
}

// ── Main screen ──────────────────────────────────────────────────────────────
const VALID_DEEP_LINK_MODES = new Set(['login', 'register', 'forgot', 'reset', 'reset-token', 'confirm']);

export default function AuthScreen() {
  const location = useLocation();
  const initialParams = (() => {
    const p = new URLSearchParams(location.search);
    const m = p.get('mode');
    return {
      mode:  VALID_DEEP_LINK_MODES.has(m) ? m : 'login',
      email: p.get('email') || '',
      token: p.get('token') || '',
    };
  })();
  const [mode, setMode]               = useState(initialParams.mode);
  const [email, setEmail]             = useState(initialParams.email);
  const [password, setPassword]       = useState('');
  const [name, setName]               = useState('');
  const [code, setCode]               = useState('');
  const [newPass, setNewPass]         = useState('');
  const [confPass, setConfPass]       = useState('');
  const [challengeUser, setChallengeUser] = useState(null);
  const [error, setError]             = useState('');
  const [info, setInfo]               = useState('');
  const [loading, setLoading]         = useState(false);

  // Token-link reset state (admin-issued one-time link)
  const resetToken = initialParams.token;
  const [tokenStatus, setTokenStatus] = useState(initialParams.mode === 'reset-token' ? 'checking' : 'idle');
  const [tokenEmail, setTokenEmail]   = useState('');

  const { signIn, signUp, confirmSignUp, resendCode, forgotPassword, resetPassword, federatedSignIn, completeNewPassword } = useAuth();
  const { t, lang, setLang } = useTranslation();

  // Deep-linked password reset (legacy ?mode=reset path — show "code sent" hint)
  useEffect(() => {
    if (initialParams.mode === 'reset' && initialParams.email) {
      setInfo(t('auth.codeSentTo', initialParams.email));
    }
  }, [initialParams.mode, initialParams.email, t]);

  // Token-link reset: validate the token on mount so the user sees a clear
  // "expired/invalid" message before being asked for a new password.
  useEffect(() => {
    if (initialParams.mode !== 'reset-token') return;
    if (!initialParams.token) {
      setTokenStatus('invalid');
      setError('Reset link is missing its token. Please ask an admin to send a new one.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await authApi.validateResetToken(initialParams.token);
        if (cancelled) return;
        if (r?.valid) {
          setTokenStatus('valid');
          setTokenEmail(r.email || '');
        } else {
          setTokenStatus('invalid');
          setError(r?.error || 'This reset link is no longer valid.');
        }
      } catch (err) {
        if (cancelled) return;
        setTokenStatus('invalid');
        setError(err.message || 'This reset link is no longer valid.');
      }
    })();
    return () => { cancelled = true; };
  }, [initialParams.mode, initialParams.token]);

  async function handleResetWithToken(e) {
    e.preventDefault(); setError(''); setLoading(true);
    if (newPass !== confPass) { setError(t('auth.passwordMismatch')); setLoading(false); return; }
    if (newPass.length < 8)  { setError(t('auth.passwordMinHint'));  setLoading(false); return; }
    try {
      await authApi.resetWithToken(resetToken, newPass);
      setMode('login');
      setNewPass(''); setConfPass('');
      if (tokenEmail) setEmail(tokenEmail);
      setInfo(t('auth.resetSuccess'));
      // Clear the token from the URL so it can't be reused or shoulder-surfed
      window.history.replaceState({}, '', '/auth');
    } catch (err) {
      setError(err.message || t('auth.resetFailed'));
    } finally { setLoading(false); }
  }

  async function handleLogin(e) {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const u = await signIn(email, password);
      if (u?.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setChallengeUser(u);
        setNewPass(''); setConfPass('');
        setMode('forceChange');
      }
    }
    catch (err) {
      if (err.code === 'UserNotConfirmedException') { setMode('confirm'); setInfo(t('auth.pleaseConfirmFirst')); }
      else if (err.message?.includes('Federation requires') || err.message?.includes('No user pool')) { setError(t('auth.configError')); }
      else { setError(err.message || t('auth.signInFailed')); }
    } finally { setLoading(false); }
  }

  async function handleForceChange(e) {
    e.preventDefault(); setError(''); setLoading(true);
    if (newPass !== confPass) { setError(t('auth.passwordMismatch')); setLoading(false); return; }
    if (newPass.length < 8)  { setError(t('auth.passwordMinHint'));  setLoading(false); return; }
    try { await completeNewPassword(challengeUser, newPass); }
    catch (err) { setError(err.message || t('auth.signInFailed')); }
    finally { setLoading(false); }
  }

  async function handleRegister(e) {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      await signUp(name, email, password);
      setMode('confirm');
      setInfo(t('auth.verificationSentTo', email));
    } catch (err) {
      if (err.message?.includes('Federation requires') || err.message?.includes('No user pool')) { setError(t('auth.configError')); }
      else { setError(err.message || t('auth.registrationFailed')); }
    } finally { setLoading(false); }
  }

  async function handleConfirm(e) {
    e.preventDefault(); setError(''); setLoading(true);
    try { await confirmSignUp(email, code); setMode('login'); setInfo(t('auth.confirmSuccess')); }
    catch (err) { setError(err.message || t('auth.confirmFailed')); }
    finally { setLoading(false); }
  }

  async function handleForgot(e) {
    e.preventDefault(); setError(''); setLoading(true);
    try { await forgotPassword(email); setMode('reset'); setInfo(t('auth.codeSentTo', email)); }
    catch (err) { setError(err.message || t('auth.sendResetFailed')); }
    finally { setLoading(false); }
  }

  async function handleReset(e) {
    e.preventDefault(); setError(''); setLoading(true);
    try { await resetPassword(email, code, newPass); setMode('login'); setInfo(t('auth.resetSuccess')); }
    catch (err) { setError(err.message || t('auth.resetFailed')); }
    finally { setLoading(false); }
  }

  function formatSocialError(err) {
    const msg = err.message || '';
    if (msg.includes('already found an entry') || msg.includes('already exists') || msg.includes('PreSignUp')) {
      return t('auth.accountExistsError');
    }
    if (msg.includes('Federation requires') || msg.includes('No user pool') || msg.includes('No Cognito')) {
      return t('auth.configError');
    }
    return msg || t('auth.socialError');
  }

  async function handleGoogle() {
    setError('');
    try { await federatedSignIn('Google'); }
    catch (err) { setError(formatSocialError(err)); }
  }

  async function handleApple() {
    setError('');
    try { await federatedSignIn('SignInWithApple'); }
    catch (err) { setError(formatSocialError(err)); }
  }

  function switchTo(m) { setMode(m); setError(''); setInfo(''); }

  return (
    <div className="auth-bg">
      <div className="auth-panel">
        <div className="auth-brand">
          <div className="auth-cross"><CrossIcon size={32} color="var(--orange)" strokeWidth={3}/></div>
          <h1 className="auth-logo">Ignite</h1>
          <p className="auth-tagline">{t('auth.tagline')}</p>
        </div>

        {/* Language toggle — visible before login so Spanish speakers can switch */}
        <div className="auth-lang-toggle">
          <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>English</button>
          <span className="auth-lang-divider">·</span>
          <button className={lang === 'es' ? 'active' : ''} onClick={() => setLang('es')}>Español</button>
        </div>

        <div className="auth-card">
          {info  && <div className="auth-info">{info}</div>}
          {error && <div className="auth-error">{error}</div>}

          {/* ── Login ── */}
          {mode === 'login' && (
            <>
              <h2 className="auth-title">{t('auth.welcomeBack')}</h2>
              <SocialButtons t={t} onGoogle={handleGoogle} onApple={handleApple} loading={loading}/>
              <OrDivider label={t('common.or')}/>
              <form onSubmit={handleLogin}>
                <div className="input-group">
                  <label>{t('auth.email')}</label>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder={t('auth.emailPlaceholder')} required/>
                </div>
                <div className="input-group">
                  <label>{t('auth.password')}</label>
                  <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder={t('auth.passwordPlaceholder')} required/>
                </div>
                <button className="btn btn-primary auth-submit" type="submit" disabled={loading}>
                  {loading ? <span className="spinner"/> : t('auth.signIn')}
                </button>
              </form>
              <div className="auth-links">
                <button onClick={()=>switchTo('forgot')}>{t('auth.forgotPassword')}</button>
                <button onClick={()=>switchTo('register')}>{t('auth.createAccount')}</button>
              </div>
              <p className="auth-legal-notice">
                {t('auth.legalNoticeSignIn')}{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer">{t('auth.termsOfService')}</Link>
                {' '}{t('auth.legalAnd')}{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer">{t('auth.privacyPolicy')}</Link>.
              </p>
            </>
          )}

          {/* ── Register ── */}
          {mode === 'register' && (
            <>
              <h2 className="auth-title">{t('auth.joinCommunity')}</h2>
              <SocialButtons t={t} onGoogle={handleGoogle} onApple={handleApple} loading={loading}/>
              <OrDivider label={t('common.or')}/>
              <form onSubmit={handleRegister}>
                <div className="input-group">
                  <label>{t('auth.fullName')}</label>
                  <input type="text" value={name} onChange={e=>setName(e.target.value)} placeholder={t('auth.namePlaceholder')} required/>
                </div>
                <div className="input-group">
                  <label>{t('auth.email')}</label>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder={t('auth.emailPlaceholder')} required/>
                </div>
                <div className="input-group">
                  <label>{t('auth.password')}</label>
                  <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder={t('auth.passwordMinHint')} required minLength={8}/>
                </div>
                <button className="btn btn-primary auth-submit" type="submit" disabled={loading}>
                  {loading ? <span className="spinner"/> : t('auth.createAccountBtn')}
                </button>
              </form>
              <div className="auth-links">
                <button onClick={()=>switchTo('login')}>{t('auth.backToSignIn')}</button>
              </div>
              <p className="auth-legal-notice">
                {t('auth.legalNoticeSignUp')}{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer">{t('auth.termsOfService')}</Link>
                {' '}{t('auth.legalAnd')}{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer">{t('auth.privacyPolicy')}</Link>.
              </p>
            </>
          )}

          {/* ── Confirm email ── */}
          {mode === 'confirm' && (
            <>
              <h2 className="auth-title">{t('auth.checkEmail')}</h2>
              <p className="auth-desc">{t('auth.enterCode')}</p>
              <form onSubmit={handleConfirm}>
                <div className="input-group">
                  <label>{t('auth.email')}</label>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder={t('auth.emailPlaceholder')} required/>
                </div>
                <div className="input-group">
                  <label>{t('auth.verificationCode')}</label>
                  <input type="text" value={code} onChange={e=>setCode(e.target.value)} placeholder={t('auth.codePlaceholder')} required maxLength={6}/>
                </div>
                <button className="btn btn-primary auth-submit" type="submit" disabled={loading}>
                  {loading ? <span className="spinner"/> : t('auth.confirmEmail')}
                </button>
              </form>
              <div className="auth-links">
                <button onClick={()=>resendCode(email).then(()=>setInfo(t('auth.codeResent')))}>{t('auth.resendCode')}</button>
                <button onClick={()=>switchTo('login')}>{t('auth.backToSignIn')}</button>
              </div>
            </>
          )}

          {/* ── Forgot password ── */}
          {mode === 'forgot' && (
            <>
              <h2 className="auth-title">{t('auth.resetPassword')}</h2>
              <form onSubmit={handleForgot}>
                <div className="input-group">
                  <label>{t('auth.email')}</label>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder={t('auth.emailPlaceholder')} required/>
                </div>
                <button className="btn btn-primary auth-submit" type="submit" disabled={loading}>
                  {loading ? <span className="spinner"/> : t('auth.sendResetCode')}
                </button>
              </form>
              <div className="auth-links">
                <button onClick={()=>switchTo('login')}>{t('auth.backToSignIn')}</button>
              </div>
              <p className="auth-legal-notice">
                {t('auth.legalNoticeSignUp')}{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer">{t('auth.termsOfService')}</Link>
                {' '}{t('auth.legalAnd')}{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer">{t('auth.privacyPolicy')}</Link>.
              </p>
            </>
          )}

          {/* ── Reset password ── */}
          {mode === 'reset' && (
            <>
              <h2 className="auth-title">{t('auth.enterNewPassword')}</h2>
              <form onSubmit={handleReset}>
                <div className="input-group">
                  <label>{t('auth.resetCode')}</label>
                  <input type="text" value={code} onChange={e=>setCode(e.target.value)} placeholder={t('auth.codePlaceholder')} required/>
                </div>
                <div className="input-group">
                  <label>{t('auth.newPassword')}</label>
                  <input type="password" value={newPass} onChange={e=>setNewPass(e.target.value)} placeholder={t('auth.passwordMinHint')} required minLength={8}/>
                </div>
                <button className="btn btn-primary auth-submit" type="submit" disabled={loading}>
                  {loading ? <span className="spinner"/> : t('auth.resetPasswordBtn')}
                </button>
              </form>
            </>
          )}

          {/* ── Reset password via admin-issued one-time token link ── */}
          {mode === 'reset-token' && (
            <>
              <h2 className="auth-title">{t('auth.enterNewPassword')}</h2>
              {tokenStatus === 'checking' && (
                <p className="auth-desc"><span className="spinner" style={{width:14,height:14,borderWidth:2,verticalAlign:'middle',marginRight:8}}/>Validating reset link…</p>
              )}
              {tokenStatus === 'invalid' && (
                <>
                  <p className="auth-desc">This reset link is no longer valid. It may have expired (links last 1 hour) or already been used.</p>
                  <div className="auth-links">
                    <button onClick={()=>switchTo('login')}>{t('auth.backToSignIn')}</button>
                  </div>
                </>
              )}
              {tokenStatus === 'valid' && (
                <>
                  {tokenEmail && <p className="auth-desc">Setting a new password for <strong>{tokenEmail}</strong>.</p>}
                  <form onSubmit={handleResetWithToken}>
                    <div className="input-group">
                      <label>{t('auth.newPassword')}</label>
                      <input type="password" value={newPass} onChange={e=>setNewPass(e.target.value)} placeholder={t('auth.passwordMinHint')} required minLength={8} autoFocus/>
                    </div>
                    <div className="input-group">
                      <label>{t('auth.confirmPassword')}</label>
                      <input type="password" value={confPass} onChange={e=>setConfPass(e.target.value)} placeholder={t('auth.passwordMinHint')} required minLength={8}/>
                    </div>
                    <button className="btn btn-primary auth-submit" type="submit" disabled={loading}>
                      {loading ? <span className="spinner"/> : t('auth.resetPasswordBtn')}
                    </button>
                  </form>
                </>
              )}
            </>
          )}

          {/* ── Force password change (admin-created accounts) ── */}
          {mode === 'forceChange' && (
            <>
              <h2 className="auth-title">{t('auth.setNewPasswordTitle')}</h2>
              <p className="auth-desc">{t('auth.setNewPasswordDesc')}</p>
              <form onSubmit={handleForceChange}>
                <div className="input-group">
                  <label>{t('auth.newPassword')}</label>
                  <input type="password" value={newPass} onChange={e=>setNewPass(e.target.value)} placeholder={t('auth.passwordMinHint')} required minLength={8}/>
                </div>
                <div className="input-group">
                  <label>{t('auth.confirmPassword')}</label>
                  <input type="password" value={confPass} onChange={e=>setConfPass(e.target.value)} placeholder={t('auth.passwordMinHint')} required minLength={8}/>
                </div>
                <button className="btn btn-primary auth-submit" type="submit" disabled={loading}>
                  {loading ? <span className="spinner"/> : t('auth.setPasswordBtn')}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="auth-verse">{t('auth.verse')}</p>
      </div>
    </div>
  );
}
